import React, { useRef, useState, useEffect } from "react";
import Button from "../components/common/Button";
import Card from "../components/common/Card";
import { GraphComponent } from "../components/chat/formatters/GraphFormatter";
import AIChatResponseWidget from "../components/common/landing-page/AIChatResponseWidget";
import MobileJourneyGraphView from "../components/common/landing-page/MobileJourneyGraphView";
import { initiateWhatsAppChat, initiatePhoneCall, initiateEmail } from "../utils/utils";
import { useToast } from "../components/common/Toast";
import { Urls } from "../constants/constants";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Icon = ({ name, className = "" }) => (
  <span className={`material-symbols-outlined ${className}`}>{name}</span>
);

// Light theme config that matches the landing page palette
const CHART_THEME = {
  colors: {
    primary: "#E08A08",
    secondary: "#3b82f6",
    accent: "#10b981",
    warning: "#8b5cf6",
    info: "#f43f5e",
    success: "#06B6D4",
    text: "#171717",
    textSecondary: "#737373",
    cardBackground: "#ffffff",
    cardBorder: "#e5e7eb",
    surfaceElevated: "#f9fafb",
    border: "#e5e7eb",
  },
};

// ─── Sections ─────────────────────────────────────────────────────────────────

function Navbar({ onGetStarted }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm" : "bg-white/60 backdrop-blur-md"}`}>
      <div className="flex justify-between items-center px-6 md:px-12 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="SchoolLine Logo" className="h-10 w-auto object-contain" />
          <div className="text-2xl font-bold tracking-tight text-neutral-900">SchoolLine</div>
        </div>
        <div className="hidden md:flex items-center gap-8">
          {[
            { title: "Holistic Report", id: "holistic-report" },
            { title: "Ask Me AI", id: "ask-me-ai" },
            { title: "Student Journey", id: "student-journey" },
            { title: "Poster Generation", id: "poster-generation" }
          ].map((item) => (
            <a 
              key={item.title} 
              href={`#${item.id}`} 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-sm font-semibold text-neutral-600 hover:text-primary transition-colors cursor-pointer"
            >
              {item.title}
            </a>
          ))}
        </div>
        {/* <Button variant="primary" size="sm" onClick={initiateWhatsAppChat}>Get Started</Button> */}
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Full-bleed background image */}
      <img
        src="/assets/testimonials/illustration.webp"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />

      {/* Left-heavy overlay so left-side text stays legible */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-white/10 pointer-events-none" />

      {/* Subtle colour glow accents */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Content sits above background */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
        <div className="max-w-2xl animate-fade-in-up">
          <span className="inline-block px-4 py-1.5 bg-primary-light/40 text-primary text-xs font-bold rounded-full mb-6 uppercase tracking-wider">
            Education Elevated
          </span>
          <h1 className="text-5xl md:text-7xl font-extrabold text-neutral-900 leading-[1.1] tracking-tight mb-8">
            The Smarter Way <br />
            <span className="text-primary">to Stay in School</span>
          </h1>
          <p className="text-xl text-neutral-600 font-medium max-w-xl mb-10 leading-relaxed">
            AI-powered school management with AI powered analytics, holistic reporting, and simplified school operations.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button variant="primary" size="lg" onClick={initiateWhatsAppChat}>Book a Demo</Button>
            <Button variant="outline" size="lg">Explore Features</Button>
          </div>
        </div>
      </div>
    </section>
  );
}


function ValueStrip() {
  const items = [
    { icon: "psychology", label: "AI-Powered Reporting" },
    { icon: "trending_up", label: "Holistic Student Growth" },
    { icon: "analytics", label: "AI-Powered Analytics" },
    { icon: "visibility", label: "Parent-School Transparency" },
  ];
  return (
    <section className="bg-neutral-50 py-12 border-y border-neutral-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap justify-between gap-8 md:gap-4">
        {items.map(({ icon, label }) => (
          <div key={label} className="flex items-center gap-3">
            <Icon name={icon} className="text-primary text-3xl" />
            <span className="font-bold text-neutral-800">{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProgressReportSection() {
  const subjects = [
    { name: "Mathematics", pct: 92, grade: "A+" },
    { name: "Science", pct: 88, grade: "A" },
    { name: "English", pct: 85, grade: "A" },
    { name: "Social Science", pct: 79, grade: "B+" },
    { name: "Hindi", pct: 80, grade: "B+" },
  ];

  const coCurriculars = [
    { name: "Math Olympiad", category: "Academic", result: "1st Place", medal: "🥇", medalColor: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    { name: "Football",      category: "Sports",   result: "Runner Up", medal: "🥈", medalColor: "bg-slate-50 text-slate-600 border-slate-200" },
    { name: "Basketball",    category: "Sports",   result: "3rd Place", medal: "🥉", medalColor: "bg-orange-50 text-orange-700 border-orange-200" },
  ];

  const getBarColor = (pct) => {
    if (pct >= 90) return "from-green-400 to-emerald-500";
    if (pct >= 80) return "from-primary to-yellow-500";
    return "from-primary-light to-primary-light";
  };

  const getGradeBadgeColor = (grade) => {
    if (grade === "A+") return "bg-green-100 text-green-700";
    if (grade === "A")  return "bg-primarymerald-50 text-emerald-700";
    return "bg-blue-50 text-primary";
  };

  return (
    <section id="holistic-report" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        {/* Description */}
        <div className="lg:col-span-4 lg:sticky top-32">
          <h2 className="text-4xl font-extrabold text-neutral-900 leading-tight mb-6">
            Beyond Traditional Grading: The Holistic Progress Report
          </h2>
          <p className="text-neutral-500 font-medium mb-8">
            Fully customizable HPC builder. Create custom sections for academic subjects and co-curricular dimensions.
          </p>
          <ul className="space-y-4">
            {["Academic Excellence in Maths & Science", "Competency in English & Languages", "360-Degree Skill Assessment"].map((text) => (
              <li key={text} className="flex items-center gap-3 text-neutral-800 font-semibold">
                <Icon name="check_circle" className="text-primary" />
                {text}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Premium Report Card ── */}
        <div className="lg:col-span-8 rounded-2xl overflow-hidden shadow-xl border border-neutral-100">

          {/* Card header with gradient */}
          <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 px-8 py-7 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 border border-primary/40 flex items-center justify-center flex-shrink-0">
                <Icon name="person" className="text-primary text-3xl" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-white">Aravind Sharma</h3>
                <p className="text-neutral-400 text-sm">Grade 9 • Section B • Roll No: 42</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="px-2.5 py-0.5 bg-primary/20 text-primary text-[10px] font-extrabold rounded-full uppercase tracking-widest border border-primary/30">
                    Term 2 • 2024
                  </span>
                  <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] font-extrabold rounded-full uppercase tracking-widest border border-amber-500/30">
                    Draft Report
                  </span>
                </div>
              </div>
            </div>
            {/* Overall GPA badge */}
            <div className="text-center hidden sm:block">
              <p className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest mb-1">Overall</p>
              <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex flex-col items-center justify-center">
                <span className="text-2xl font-black text-white">A</span>
                <span className="text-[10px] font-bold text-primary">88.8%</span>
              </div>
            </div>
          </div>

          {/* Card body */}
          <div className="bg-white p-8 grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* ── Academic bars ── */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Icon name="school" className="text-primary text-lg" />
                <h4 className="text-xs font-extrabold uppercase tracking-widest text-neutral-500">Core Academic Performance</h4>
              </div>
              <div className="space-y-4">
                {subjects.map(({ name, pct, grade }) => (
                  <div key={name}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-medium text-neutral-700">{name}</span>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-extrabold rounded ${getGradeBadgeColor(grade)}`}>{grade}</span>
                        <span className="text-sm font-black text-neutral-800">{pct}%</span>
                      </div>
                    </div>
                    <div className="h-2.5 w-full bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${getBarColor(pct)} rounded-full transition-all duration-700`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Co-curricular ── */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <Icon name="emoji_events" className="text-primary text-lg" />
                <h4 className="text-xs font-extrabold uppercase tracking-widest text-neutral-500">Co-curricular Achievements</h4>
              </div>
              <div className="space-y-3">
                {coCurriculars.map(({ name, category, result, medal, medalColor }) => (
                  <div
                    key={name}
                    className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl border border-neutral-100 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center text-xl ${medalColor}`}>
                        {medal}
                      </div>
                      <div>
                        <p className="font-bold text-sm text-neutral-800">{name}</p>
                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">{category}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-lg text-xs font-extrabold border ${medalColor}`}>
                      {result}
                    </span>
                  </div>
                ))}
              </div>

              {/* Teacher remark */}
              <div className="mt-5 p-4 bg-primary/5 rounded-xl border-l-4 border-primary">
                <p className="text-xs text-neutral-500 italic leading-relaxed">
                  "Aravind continues to excel both academically and in sports — a well-rounded performer."
                </p>
                <p className="text-[10px] text-neutral-400 font-bold mt-1.5 uppercase tracking-wider">— Class Teacher</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AskMeSection() {
  const modes = [
    {
      icon: "school",
      iconBg: "bg-primary",
      title: "Grade-Aware AI Tutor",
      desc: "Delivers explanations perfectly adapted to each student's cognitive level—answering the same question differently based on their grade. Strictly blocks non-educational queries to ensure a safe learning environment.",
      chips: ["Explain photosynthesis", "Help me with my Math homework"],
    },
    {
      icon: "assignment_ind",
      iconBg: "bg-primary",
      title: "Teacher Mode",
      desc: "Generate class performance reports and subject-wise summaries instantly with AI.",
      chips: ["Summarize Grade 10-B Math results", "Identify students needing support in English"],
    },
  ];

  return (
    <section id="ask-me-ai" className="bg-neutral-900 text-white py-24 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-extrabold mb-6 leading-tight">
            Meet 'Ask Me': Your <br />
            <span className="text-primary">Intelligent School Assistant</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Mode cards */}
          <div className="space-y-8">
            {modes.map(({ icon, iconBg, title, desc, chips }) => (
              <div key={title} className="p-8 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                <div className={`w-12 h-12 rounded-lg ${iconBg} flex items-center justify-center mb-6`}>
                  <Icon name={icon} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-4">{title}</h3>
                <p className="text-neutral-400 font-medium leading-relaxed">{desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {chips.map((c) => (
                    <span key={c} className="px-4 py-1.5 rounded-full bg-white/10 text-xs font-medium italic border border-white/5">
                      "{c}"
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Chat preview */}
          <div className="bg-white rounded-xl p-6 flex flex-col shadow-2xl min-h-[420px]">
            <div className="flex items-center gap-3 pb-5 border-b border-neutral-100">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white">
                <Icon name="auto_awesome" />
              </div>
              <div>
                <h4 className="text-neutral-900 font-bold">Ask Me AI</h4>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-[10px] font-bold text-neutral-400 uppercase">Ready to assist</span>
                </div>
              </div>
            </div>

            <div className="flex-grow py-6 space-y-5">
              <div className="flex justify-end">
                <div className="bg-neutral-100 text-neutral-800 px-4 py-3 rounded-xl rounded-tr-none text-sm font-medium max-w-[80%]">
                  How is Class 8-C performing in Science compared to last term?
                </div>
              </div>
              <AIChatResponseWidget />
            </div>

            <div className="mt-auto p-3 bg-neutral-100 rounded-xl flex items-center gap-3">
              <input
                className="bg-transparent border-none focus:outline-none text-sm flex-grow text-neutral-700 font-medium"
                placeholder="Type your request here..."
                type="text"
                readOnly
              />
              <button className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center shadow-lg">
                <Icon name="send" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute top-0 right-0 w-[50%] h-full bg-primary/5 -skew-x-12 origin-top-right pointer-events-none" />
    </section>
  );
}

// ─── Student Journey (new) ────────────────────────────────────────────────────

// Single line-chart: each dataset is a subject, each label is an academic year
const JOURNEY_CHART_DATA = {
  type: "line",
  title: "Subject Performance Across Academic Years",
  labels: ["2020-21 (Gr.6)", "2021-22 (Gr.7)", "2022-23 (Gr.8)", "2023-24 (Gr.9)"],
  datasets: [
    { label: "Mathematics", data: [72, 68, 81, 92] },  // dip in Gr7 then strong rise
    { label: "English",     data: [80, 84, 78, 85] },  // mostly steady, small dip Gr8
    { label: "Science",     data: [65, 74, 70, 88] },  // dip Gr8, big jump Gr9
    { label: "Hindi",       data: [78, 71, 76, 80] },  // slight dip Gr7, recovers
    { label: "Social Sci",  data: [70, 75, 82, 79] },  // up then slight pullback
  ],
  options: {
    xAxisTitle: "Academic Year",
    yAxisTitle: "Score (%)",
  },
};

// Per-year masonry card data
const YEAR_CARDS = [
  {
    grade: "Grade 6", year: "2020-21",
    icon: "school", badge: "Foundation Year", badgeColor: "bg-neutral-100 text-neutral-600",
    highlight: "Strong start across core subjects. Ranked in Top 20% of class.",
    tags: [
      { label: "Attendance: 97%", color: "bg-green-50 text-green-700" },
      { label: "Class Rank: 8/42", color: "bg-blue-50 text-primary-light" },
    ],
    remark: "Aravind is a sincere and attentive student who shows great promise in Mathematics.",
    strengths: ["Consistent homework submission", "Strong mental math"],
    improve: ["Needs to improve spoken Hindi"],
  },
  {
    grade: "Grade 7", year: "2021-22",
    icon: "military_tech", badge: "Olympiad Year", badgeColor: "bg-yellow-50 text-yellow-700",
    highlight: "Math dip recovered mid-year. State-level Math Olympiad distinction.",
    tags: [
      { label: "Olympiad Distinction", color: "bg-yellow-50 text-yellow-700" },
      { label: "Attendance: 94%", color: "bg-green-50 text-green-700" },
    ],
    remark: "Showed resilience after a rocky start. His curiosity in Science is commendable.",
    strengths: ["Competitive exam aptitude", "Leadership in group projects"],
    improve: ["English essay writing needs more practice"],
  },
  {
    grade: "Grade 8", year: "2022-23",
    icon: "trending_up", badge: "STEM Surge", badgeColor: "bg-blue-50 text-primary-light",
    highlight: "Exceptional STEM growth. Sports Captain. Top 5% of cohort.",
    tags: [
      { label: "Top 5%", color: "bg-green-50 text-green-700" },
      { label: "Sports Captain", color: "bg-sky-50 text-sky-700" },
      { label: "Sci Project: 1st Place", color: "bg-primary-light/30 text-primary" },
    ],
    remark: "Aravind has truly blossomed this year — both academically and as a leader.",
    strengths: ["Scientific thinking", "Peer mentoring", "Extra-curricular balance"],
    improve: ["Social Science answer presentation"],
  },
  {
    grade: "Grade 9", year: "2023-24",
    icon: "bolt", badge: "Current Session", badgeColor: "bg-primary text-white",
    highlight: "Best academic year so far. On track for Board distinction.",
    tags: [
      { label: "Maths: 92%", color: "bg-primary-light/30 text-primary" },
      { label: "Sc: 88%", color: "bg-primary-light/30 text-primary" },
      { label: "Spelling Bee: Runner Up", color: "bg-rose-50 text-rose-700" },
    ],
    remark: "One of the most improved students I have taught in 12 years. Board-ready.",
    strengths: ["Self-directed learning", "Cross-subject analytical skills", "Exam strategy"],
    improve: ["Handwriting and neat presentation in exams"],
    isActive: true,
  },
];

function YearCard({ card }) {
  return (
    <div className={`bg-white rounded-2xl border ${
      card.isActive ? "border-primary/30 ring-2 ring-primary/10 shadow-lg" : "border-neutral-100 shadow-sm"
    } p-6 flex flex-col gap-4 hover:-translate-y-1 transition-transform duration-300`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            card.isActive ? "bg-primary text-white" : "bg-neutral-100 text-primary"
          }`}>
            <Icon name={card.icon} />
          </div>
          <div>
            <h4 className="font-extrabold text-neutral-900 text-base">{card.grade}</h4>
            <span className="text-xs text-neutral-400 font-medium">{card.year}</span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${card.badgeColor}`}>
          {card.badge}
        </span>
      </div>

      {/* Highlight */}
      <p className="text-sm text-neutral-600 leading-relaxed">{card.highlight}</p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5">
        {card.tags.map((t) => (
          <span key={t.label} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${t.color}`}>{t.label}</span>
        ))}
      </div>

      {/* Teacher remark */}
      <div className="bg-neutral-50 rounded-xl px-4 py-3 border-l-4 border-primary/40">
        <p className="text-xs text-neutral-500 italic">" {card.remark} "</p>
        <p className="text-[10px] text-neutral-400 font-bold mt-1 uppercase tracking-wider">— Class Teacher</p>
      </div>

      {/* Strengths + Improve */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-extrabold text-green-600 uppercase tracking-wider mb-1.5">
            <Icon name="thumb_up" className="text-xs mr-0.5" /> Strengths
          </p>
          <ul className="space-y-1">
            {card.strengths.map((s) => (
              <li key={s} className="text-[11px] text-neutral-600 flex items-start gap-1">
                <span className="text-green-500 mt-0.5">•</span> {s}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] font-extrabold text-amber-500 uppercase tracking-wider mb-1.5">
            <Icon name="trending_up" className="text-xs mr-0.5" /> To Improve
          </p>
          <ul className="space-y-1">
            {card.improve.map((s) => (
              <li key={s} className="text-[11px] text-neutral-600 flex items-start gap-1">
                <span className="text-amber-400 mt-0.5">•</span> {s}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function StudentJourneySection() {
  const [expanded, setExpanded] = useState(false);

  return (
    <section id="student-journey" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
      {/* Section heading */}
      <div className="text-center max-w-3xl mx-auto mb-16">
        <h2 className="text-4xl font-extrabold mb-6 text-neutral-900">Track the Complete Growth Story</h2>
        <p className="text-neutral-500 font-medium text-lg leading-relaxed">
          A comprehensive view of academic and co-curricular progress across every school year.
        </p>
      </div>

      <div className="bg-white/60 backdrop-blur-3xl border border-primary/20 shadow-2xl shadow-primary/5 rounded-[2.5rem] p-6 md:p-12 relative overflow-hidden">
        {/* Glow behind unified block */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />

        {/* ── Student Summary block ── */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start mb-12 border-b border-primary/10 pb-12">
          {/* Identity */}
          <div className="lg:col-span-1">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 shadow-sm">
              <Icon name="person" className="text-primary text-3xl" />
            </div>
            <h3 className="text-2xl font-extrabold text-neutral-900">Aravind Sharma</h3>
            <p className="text-neutral-500 font-medium text-sm mb-4">Grade 9 • Section B • Roll No: 42</p>
            <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary-dark text-xs font-bold rounded-full uppercase tracking-widest border border-primary/20">
              The Relentless Achiever
            </span>
          </div>

          {/* Strong points + remark */}
          <div className="lg:col-span-1 space-y-4">
            <div>
              <p className="text-[10px] font-extrabold text-primary-dark uppercase tracking-widest mb-4">Overall Strengths</p>
              <div className="space-y-3">
                {["Top percentile in Mathematics & Science", "Exemplary leadership & sports", "Consistent upward academic trajectory", "National-level competitive exam participant"].map((s) => (
                  <div key={s} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Icon name="check" className="text-green-600 text-xs font-bold" />
                    </div>
                    <span className="text-sm font-medium text-neutral-700">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Areas for improvement + overall remark */}
          <div className="lg:col-span-1 space-y-4">
            <div>
              <p className="text-[10px] font-extrabold text-amber-600 uppercase tracking-widest mb-4">Areas for Improvement</p>
              <div className="space-y-3">
                {["English written expression", "Hindi oral communication", "Neat exam presentation"].map((s) => (
                  <div key={s} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Icon name="arrow_upward" className="text-amber-600 text-xs font-bold" />
                    </div>
                    <span className="text-sm font-medium text-neutral-700">{s}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary/5 rounded-2xl px-5 py-4 border border-primary/10 mt-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
              <p className="text-sm text-neutral-600 italic font-medium leading-relaxed">
                "Aravind represents the best of what our school nurtures — a curious, resilient, and spirited learner."
              </p>
              <p className="text-[10px] text-primary-dark font-black mt-3 uppercase tracking-wider">— Principal's Overall Remark</p>
            </div>
          </div>
        </div>

        {/* ── Single aggregated line chart ── */}
        <div className="mb-10 relative z-10 w-full">
          {/* Desktop/Web view */}
          <div className="hidden md:block w-full overflow-hidden">
            <GraphComponent graphData={JOURNEY_CHART_DATA} theme={CHART_THEME} enableAnimation />
          </div>
          {/* Mobile view */}
          <div className="block md:hidden w-full">
            <MobileJourneyGraphView graphData={JOURNEY_CHART_DATA} theme={CHART_THEME} />
          </div>
        </div>

        {/* ── Collapsible toggle ── */}
        <div className="flex justify-center mb-8 relative z-10">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary-dark shadow-lg shadow-primary/20 text-white font-bold rounded-full text-sm transition-all duration-300 transform hover:-translate-y-0.5"
          >
            <Icon name={expanded ? "keyboard_arrow_up" : "keyboard_arrow_down"} />
            {expanded ? "Hide Year-by-Year Details" : "Show Year-by-Year Details"}
          </button>
        </div>

        {/* ── Masonry grid (collapsible) ── */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10 transition-all duration-700 ease-in-out ${
            expanded ? "max-h-[4000px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
          }`}
        >
          {/* First 2 cards — same row, auto height */}
          <YearCard card={YEAR_CARDS[0]} />
          <YearCard card={YEAR_CARDS[1]} />
          {/* Last 2 cards */}
          <YearCard card={YEAR_CARDS[2]} />
          <YearCard card={YEAR_CARDS[3]} />
        </div>
      </div>
    </section>
  );
}


function AnalyticsSection() {
  const studentData = [
    { id: 1, name: "Aravind Sharma", evs: 85, math: 92, science: 88, english: 85 },
    { id: 2, name: "Meera Kumar", evs: 90, math: 85, science: 91, english: 88 },
    { id: 3, name: "Rohan Joshi", evs: 75, math: 78, science: 80, english: 75 },
    { id: 4, name: "Sanjana Tiwari", evs: 92, math: 95, science: 94, english: 90 },
    { id: 5, name: "Vikram Rathore", evs: 68, math: 65, science: 72, english: 68 },
    { id: 6, name: "Priya Patel", evs: 88, math: 88, science: 86, english: 92 },
  ];
  const stats = [
    { label: "Class Avg", value: "84%" },
    { label: "Math Avg", value: "86%" },
    { label: "Science Avg", value: "85%" },
  ];
  const bullets = [
    { icon: "insights", title: "Automated Trend Analysis", desc: "Automatically compare individual student performance against class averages in real-time." },
    { icon: "speed", title: "Instant Report Generation", desc: "Download board-compliant reports in various formats with one click." },
  ];

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Chart card */}
        <div className="order-2 lg:order-1 bg-white/60 backdrop-blur-2xl border border-white/40 rounded-3xl overflow-hidden shadow-2xl shadow-primary/10">
          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <h4 className="font-bold text-xl text-neutral-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary material-symbols-outlined text-sm">bar_chart</span>
                Class Analytics
              </h4>
              <span className="text-[10px] px-3 py-1 font-bold tracking-widest bg-primary/10 text-primary-dark rounded-full border border-primary/20">TERM 2 • 2024</span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead>
                  <tr className="border-b border-neutral-200 text-neutral-500 text-[10px] uppercase tracking-wider">
                    <th className="py-4 px-4 font-semibold">Student</th>
                    <th className="py-4 px-4 font-semibold text-right">EVS</th>
                    <th className="py-4 px-4 font-semibold text-right">Math</th>
                    <th className="py-4 px-4 font-semibold text-right">Science</th>
                    <th className="py-4 px-4 font-semibold text-right">English</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {studentData.map((d) => (
                    <tr key={d.id} className="hover:bg-primary/5 transition-colors group">
                      <td className="py-4 px-4 font-semibold text-neutral-800 group-hover:text-primary transition-colors">{d.name}</td>
                      <td className="py-4 px-4 text-right">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${d.evs >= 90 ? 'bg-green-100 text-green-700' : d.evs < 70 ? 'bg-red-100 text-red-600' : 'bg-neutral-100 text-neutral-600'}`}>{d.evs}</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${d.math >= 90 ? 'bg-green-100 text-green-700' : d.math < 70 ? 'bg-red-100 text-red-600' : 'bg-neutral-100 text-neutral-600'}`}>{d.math}</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${d.science >= 90 ? 'bg-green-100 text-green-700' : d.science < 70 ? 'bg-red-100 text-red-600' : 'bg-neutral-100 text-neutral-600'}`}>{d.science}</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${d.english >= 90 ? 'bg-primary/10 text-primary-dark' : d.english < 70 ? 'bg-red-100 text-red-600' : 'bg-neutral-100 text-neutral-600'}`}>{d.english}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {stats.map(({ label, value }) => (
                <div key={label} className="relative p-5 bg-gradient-to-br from-white to-neutral-50 rounded-xl text-center border border-neutral-200 shadow-sm overflow-hidden group hover:border-primary/50 transition-colors">
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors"></div>
                  <p className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-2 relative z-10">{label}</p>
                  <p className="text-2xl font-black text-primary relative z-10">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="order-1 lg:order-2">
          <h2 className="text-4xl font-extrabold mb-6 text-neutral-900">Real-time Analytics at Your Fingertips</h2>
          <p className="text-neutral-500 font-medium text-lg leading-relaxed mb-8">
            Stop waiting for end-of-term reports. Get daily insights into performance across subjects with our live dashboard.
          </p>
          <div className="space-y-6">
            {bullets.map(({ icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-light/30 flex items-center justify-center flex-shrink-0">
                  <Icon name={icon} className="text-primary text-xl" />
                </div>
                <div>
                  <h4 className="font-bold text-neutral-800">{title}</h4>
                  <p className="text-neutral-500 text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhySchoolLineSection() {
  const features = [
    { icon: "bolt", title: "Reduces manual work", desc: "Automate 70% of repetitive administrative tasks for teachers and staff." },
    { icon: "data_usage", title: "Modern reporting", desc: "Go beyond numbers with qualitative feedback and AI-driven growth tracking." },
    { icon: "chat_bubble_outline", title: "Improves parent communication", desc: "Seamless, real-time updates that build trust between home and school." },
    { icon: "dynamic_feed", title: "Academics & Activities", desc: "A unified platform that values every aspect of a student's journey." },
  ];

  return (
    <section className="bg-neutral-50 py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold mb-4 text-neutral-900">Why Educational Leaders Choose Us</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map(({ icon, title, desc }) => (
            <div key={title} className="p-8 bg-white rounded-xl border border-neutral-100 hover:-translate-y-1 transition-transform duration-300">
              <Icon name={icon} className="text-primary text-4xl mb-6 block" />
              <h4 className="font-bold text-lg mb-3 text-neutral-800">{title}</h4>
              <p className="text-neutral-500 text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
      <div className="bg-neutral-900 rounded-[2rem] p-12 md:p-20 text-center relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-8">
            Ready to modernize <br /> your school?
          </h2>
          <p className="text-neutral-400 text-lg md:text-xl font-medium mb-12 max-w-2xl mx-auto">
            Join hundreds of progressive schools already transforming education with SchoolLine.
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <Button variant="primary" size="lg" onClick={initiateWhatsAppChat}> Speak with an Expert</Button>
            {/* <button onClick={initiatePhoneCall} className="px-10 py-4 bg-white/10 text-white rounded-lg font-black text-lg hover:bg-white/20 transition-colors">
              Speak with an Expert
            </button> */}
          </div>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
      </div>
    </section>
  );
}

function LandingFooter() {
  const { showToast, ToastComponent } = useToast();

  const handleShareClick = () => {
    navigator.clipboard.writeText(Urls.LANDING_PAGE);
    showToast("Link copied to clipboard!", "success");
  };

  return (
    <>
    <footer className="bg-neutral-50 w-full border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          
          {/* Logo & Copyright */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left gap-1.5">
            <div className="flex items-center gap-2">
               <img src="/assets/logo.png" alt="SchoolLine Logo" className="h-6 w-auto object-contain" />
               <div className="text-lg font-black text-neutral-900 tracking-tight">SchoolLine</div>
            </div>
            <p className="text-xs text-neutral-500">
              © 2026 goodONE Solutions. All rights reserved.
            </p>
          </div>
          
          {/* Quick Links & Icons */}
          <div className="flex flex-wrap items-center justify-center gap-6">
            <button onClick={initiateWhatsAppChat} className="text-sm font-semibold text-neutral-600 hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0 flex items-center gap-1.5">
              <Icon name="chat" className="text-[18px]" />
              Talk to Sales
            </button>
            <button onClick={initiateEmail} className="text-sm font-semibold text-neutral-600 hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0 flex items-center gap-1.5">
              <Icon name="mail" className="text-[18px]" />
              Contact Support
            </button>
            
            <div className="h-4 w-px bg-neutral-300 hidden md:block"></div>
            
            <div className="flex items-center gap-3">
              <button onClick={handleShareClick} className="text-neutral-400 hover:text-primary transition-colors bg-transparent border-none p-0 cursor-pointer flex items-center">
                <Icon name="share" className="text-xl" />
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </footer>
    {ToastComponent}
    </>
  );
}

// ─── PosterSection ─────────────────────────────────────────────────────────────

function PosterSection() {
  return (
    <section id="poster-generation" className="relative w-full py-24 bg-white overflow-hidden flex items-center justify-center border-t border-neutral-100">
      {/* Aesthetic Image on the right with an artistic 'paint' mask fade */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-3/5 z-0 pointer-events-none">
        <div className="w-full h-full relative"
             style={{
               // Creates an irregular, organic shape fading into the left side
               WebkitMaskImage: 'radial-gradient(ellipse at 85% 50%, black 30%, transparent 70%), radial-gradient(circle at 60% 20%, black 5%, transparent 45%), radial-gradient(circle at 65% 85%, black 5%, transparent 50%)',
               maskImage: 'radial-gradient(ellipse at 85% 50%, black 30%, transparent 70%), radial-gradient(circle at 60% 20%, black 5%, transparent 45%), radial-gradient(circle at 65% 85%, black 5%, transparent 50%)',
               WebkitMaskComposite: 'source-over',
               maskComposite: 'add'
             }}
        >
          <img 
            src="/assets/features/poster_image.webp" 
            alt="AI Poster Generator" 
            className="w-full h-full object-cover object-center opacity-10 lg:opacity-90 mix-blend-multiply"
          />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          <div className="text-neutral-900 space-y-8">
            <span className="inline-block px-4 py-1.5 bg-primary/10 border border-primary/20 text-primary-dark font-bold rounded-full uppercase tracking-widest text-xs">
              AI Powered Graphic Design
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-neutral-900">
              Instant AI Posters for <br />
              <span className="text-primary">Every Milestone</span>
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed max-w-xl">
              Celebrate your students' achievements or announce special events instantly without graphic design struggles. Simply type the content!
            </p>
            
            <div className="space-y-4">
              {[
                { icon: "psychology", title: "Smart Recognition", desc: "AI automatically identifies the occasion and structural needs from your text." },
                { icon: "palette", title: "Automated Theming", desc: "Dynamically applies stunning design templates that perfectly match the event's vibe." },
                { icon: "branding_watermark", title: "Brand Perfect", desc: "Your school's logo and custom colors are automatically seamlessly integrated." }
              ].map((feature, i) => (
                <div key={i} className="flex gap-5 items-start bg-white p-5 rounded-3xl border border-neutral-200 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all group">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name={feature.icon} className="text-primary text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-neutral-900 font-bold text-lg mb-1">{feature.title}</h4>
                    <p className="text-neutral-500 text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button 
              variant="primary" 
              size="lg" 
              className="mt-8 rounded-full shadow-lg shadow-primary/30" 
              leftIcon={<Icon name="auto_awesome" />} 
              onClick={initiateWhatsAppChat}
            >
              Learn more
            </Button>
          </div>
          
          {/* Empty spacer div to let the background image shine through on the right */}
          <div className="hidden lg:block relative h-[600px]"></div>
          
        </div>
      </div>
    </section>
  );
}

// ─── OtherFeaturesSection ────────────────────────────────────────────────────

function OtherFeaturesSection() {
  const otherFeatures = [
    { icon: "menu_book", title: "Online Diary", desc: "An online diary system where teachers post daily diaries and students can easily view them.", color: "text-blue-600", bg: "bg-blue-100/50" },
    { icon: "emoji_events", title: "Rank List", 
      desc: "Generate internal ranks for competitions or exams. Instantly published across the student home screen.", color: "text-amber-600", bg: "bg-amber-100/50" },
    { icon: "celebration", title: "School Events", desc: "Digitally preserve special memories. Capture and share any events held by the school for parents and students.", color: "text-pink-600", bg: "bg-pink-100/50" },
    { icon: "assignment_turned_in", title: "Online Marksheets", desc: "Academic marksheets completely online. Parents and students can seamlessly view and print scorecards.", color: "text-green-600", bg: "bg-green-100/50" },
    { icon: "assignment", title: "Assignments", desc: "Rich assignments with text, photos, documents, and voice. Teachers can evaluate, mark incomplete, or request rewrites.", color: "text-purple-600", bg: "bg-purple-100/50" },
    { icon: "smart_display", title: "Video Lessons", desc: "Access high-quality online video libraries for complex lessons and classroom references.", color: "text-red-600", bg: "bg-red-100/50" },
    { icon: "description", title: "Chapter Notes", desc: "Organized notes for every chapter ensuring students have easy access to course materials anytime.", color: "text-cyan-600", bg: "bg-cyan-100/50" },
    { icon: "school", title: "Revision Hub", desc: "Crucial study notes and previous year question papers compiled for quick reference during exam seasons.", color: "text-primary-dark", bg: "bg-primary/20" },
    { icon: "how_to_reg", title: "Online Attendance", desc: "Live attendance marking system that instantly notifies parents regarding their ward's absence.", color: "text-indigo-600", bg: "bg-indigo-100/50" },
  ];

  return (
    <section className="py-24 bg-neutral-50/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl font-extrabold mb-6 text-neutral-900">A Comprehensive Ecosystem</h2>
          <p className="text-neutral-500 font-medium text-lg leading-relaxed">
            Discover a suite of powerful features designed to simplify daily academics and bridge communications.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherFeatures.map((feature, idx) => (
             <div key={idx} className="bg-white rounded-[2rem] p-8 border border-neutral-200 shadow-xl shadow-neutral-200/40 hover:shadow-2xl hover:shadow-primary/10 hover:border-primary/30 transition-all duration-300 group flex flex-col items-start text-left hover:-translate-y-1">
               <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3 ${feature.bg}`}>
                 <Icon name={feature.icon} className={`text-3xl ${feature.color}`} />
               </div>
               <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-primary transition-colors">{feature.title}</h3>
               <p className="text-neutral-500 leading-relaxed text-sm">
                 {feature.desc}
               </p>
             </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Landing Footer ────────────────────────────────────────────────────────────


export default function LandingPage() {
  return (
    <>
      {/* Google Fonts + Material Symbols */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap" rel="stylesheet" />

      <div className="legacy-type bg-white text-neutral-900 selection:bg-primary-light/40">
        <Navbar />
        <main>
          <HeroSection />
          <ValueStrip />
          <ProgressReportSection />
          <AskMeSection />
          <StudentJourneySection />
          <PosterSection />
          <AnalyticsSection />
          <OtherFeaturesSection />
          <WhySchoolLineSection />
          <CTASection />
        </main>
        <LandingFooter />
      </div>
    </>
  );
}